import { AxiosResponse } from 'axios';
import {
  LocationStorage,
  Insert,
  SelectionConfig,
  SelectionConfigItem,
  Option,
  InjectionMachine,
  InputInsert,
} from '../../types/mold';
import api from './client';

export const locationStorageService = {
  getLocations: async (): Promise<AxiosResponse<LocationStorage[]>> => {
    return api.get('/mold-locations');
  },
  getAll: async (): Promise<Option[]> => {
    const response = await locationStorageService.getLocations();
    try {
      return response.data.map((location) => ({
        value: location.name,
        label: location.name,
      }));
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  },
  createLocation: async ({
    name,
    type = 'storage',
  }: {
    name: string;
    type?: string;
  }): Promise<AxiosResponse<LocationStorage[]>> => {
    return api.post('/mold-locations', { name, type });
  },
  deleteLocation: async (id: number): Promise<void> => {
    return api.delete(`/mold-locations/${id}`);
  },
};

export const InsertService = {
  getInserts: async (): Promise<AxiosResponse<Insert[]>> => {
    return api.get('/inserts');
  },
  createInsert: async (data: {
    name: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<Insert>> => {
    return api.post('/inserts', data);
  },
  updateInsert: async (
    id: number,
    data: InputInsert,
  ): Promise<AxiosResponse<Insert>> => {
    return api.patch(`/inserts/${id}`, data);
  },
};

export const selectionConfigService = {
  nameEnums: {
    ReporterConfirmRejectReasons: 'reporter_confirm_reject_reasons',
  },

  // TODO: To reconciliate createSelectionConfigValue and createNewSelectionConfigValue
  createSelectionConfigValue: async (
    name: string,
    value: string,
  ): Promise<AxiosResponse<{ message: string }>> => {
    return api.post(`/selection-configs/${name}`, { value });
  },

  createNewSelectionConfigValue: async (
    name: string,
    value: string,
  ): Promise<AxiosResponse<void>> => {
    try {
      const response = await api.post(`/selection-configs/${name}`, { value });
      return response;
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        throw new Error('Selection config value already exists');
      }
      throw error;
    }
  },

  getSelectionConfig: async (
    name: string,
  ): Promise<AxiosResponse<SelectionConfigItem>> => {
    try {
      const response = await api.get(`/selection-configs/${name}`);
      return response;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        throw new Error('Selection config not found');
      }
      throw error;
    }
  },

  updateSelectionConfig: async (
    name: string,
    data: { value: SelectionConfig[] },
  ): Promise<AxiosResponse<SelectionConfigItem>> => {
    return api.patch(`/selection-configs/${name}`, data);
  },

  createPartMatGradeOption: async (value: string): Promise<Option> => {
    await selectionConfigService.createSelectionConfigValue(
      'product_part_materials',
      value,
    );
    return { value, label: value };
  },

  deleteSelectionConfigValue: async (
    name: string,
    id: string,
  ): Promise<AxiosResponse<void>> => {
    try {
      const response = await api.delete(`/selection-configs/${name}/${id}`);
      return response;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        throw new Error('Value not found');
      }
      if (error.response && error.response.status === 400) {
        throw new Error('Invalid request');
      }
      if (error.response && error.response.status === 500) {
        throw new Error('Failed to delete selection config value');
      }
      throw error;
    }
  },
};

export const injectionMachineService = {
  getInjectionMachines: async (): Promise<
    AxiosResponse<InjectionMachine[]>
  > => {
    return api.get('/injection-machines');
  },

  getOptions: async (): Promise<Option[]> => {
    const response = await injectionMachineService.getInjectionMachines();
    return response.data.map((machine) => ({
      value: machine.name,
      label: machine.name,
    }));
  },

  getInjectionMachineById: async (
    id: number,
  ): Promise<AxiosResponse<InjectionMachine>> => {
    return api.get(`/injection-machines/${id}`);
  },

  createInjectionMachine: async (data: {
    name: string;
    manufacturer: string;
    model: string;
    serialNumber: string;
    location: string;
    tonnage: number;
    screwDiameter: number;
    isActive?: boolean;
  }): Promise<AxiosResponse<InjectionMachine>> => {
    return api.post('/injection-machines', data);
  },

  updateInjectionMachine: async (
    id: number,
    data: {
      name?: string;
      manufacturer?: string;
      model?: string;
      serialNumber?: string;
      location?: string;
      tonnage?: number;
      screwDiameter?: number;
      isActive?: boolean;
    },
  ): Promise<AxiosResponse<InjectionMachine>> => {
    return api.patch(`/injection-machines/${id}`, data);
  },

  deleteInjectionMachine: async (id: number): Promise<AxiosResponse<void>> => {
    return api.delete(`/injection-machines/${id}`);
  },
};
